import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  LinkBox,
  LinkOther,
  LinkOverlay,
} from '@sorare/core/src/atoms/navigation/Box';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { SealingOnboardingFlow } from 'components/sealing/SealingOnboardingFlow';
import rewardsBannerBg from 'components/sealing/assets/sealing-banner-bg.webp';

const Root = styled(LinkBox)`
  position: relative;
  isolation: isolate;
  display: block;
  width: 100%;
  background: no-repeat left / cover url(${rewardsBannerBg});
  padding: var(--triple-unit);
  border: 2px solid var(--c-white);
  border-radius: var(--double-unit);

  @media ${tabletAndAbove} {
    background-position: center;
  }
`;

const FlexLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--unit);

  @media ${tabletAndAbove} {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const TextContent = styled(Vertical)`
  align-items: flex-start;
  text-align: left;
`;

const CloseButton = styled(LinkOther).attrs({ as: IconButton })`
  position: absolute;
  right: var(--unit);
  top: var(--unit);
`;

const StyledChip = styled(Chip)`
  text-transform: uppercase;
`;

const subtitleBySport = defineMessages({
  [Sport.BASEBALL]: {
    id: 'SealingOnboarding.Banner.subtitle',
    defaultMessage:
      'Sealing cards increases your Vault and Player Loyalty levels, unlocking perks like boosted gameplay rewards.',
  },
  [Sport.NBA]: {
    id: 'SealingOnboarding.Banner.footballSubtitle',
    defaultMessage:
      'Sealing cards increases your vault and unlocked boosted rewards',
  },
  [Sport.FOOTBALL]: {
    id: 'SealingOnboarding.Banner.footballSubtitle',
    defaultMessage:
      'Sealing cards increases your vault and unlocked boosted rewards',
  },
});

export const SealingOnboardingBanner = () => {
  const { sport } = useSportContext();
  const [showOnboardingFlow, setShowOnboardingFlow] = useState(false);
  const { update: updateLifecycle } = useLifecycle();

  return (
    <>
      {!!showOnboardingFlow && (
        <SealingOnboardingFlow
          isOpen={showOnboardingFlow}
          setIsOpen={setShowOnboardingFlow}
        />
      )}
      <Root onClick={() => setShowOnboardingFlow(true)}>
        <FlexLayout>
          <TextContent gap={0.5}>
            <Horizontal>
              <StyledChip color="purple">
                <FormattedMessage
                  id="ClubShop.Item.Label.New"
                  defaultMessage="New"
                />
              </StyledChip>
              <LabelL bold>
                <FormattedMessage
                  id="SealingOnboarding.Banner.title"
                  defaultMessage="Seal your cards"
                />
              </LabelL>
            </Horizontal>
            <LabelM color="var(--c-nd-600)">
              <FormattedMessage {...subtitleBySport[sport]} />
            </LabelM>
          </TextContent>
          <LinkOverlay as={Button} size="small">
            <FormattedMessage
              id="SealingOnboarding.Banner.cta"
              defaultMessage="Learn more"
            />
          </LinkOverlay>
        </FlexLayout>
        <CloseButton
          icon={faXmark}
          size="compact"
          color="transparent"
          onClick={async () =>
            await updateLifecycle(LIFECYCLE.sawMlbSealingOnboarding, true)
          }
        />
      </Root>
    </>
  );
};
